/**
 * Snapshot -> AG Grid transaction.
 *
 * The screener socket pushes a **full snapshot** on every refresh, never a
 * delta. Handing that straight to `rowData` makes AG Grid tear down and rebuild
 * every row, which destroys selection, scroll position and cell focus on each
 * tick. Diffing against the ids already in the grid turns the same push into an
 * add/update/remove transaction that leaves untouched rows — and all their UI
 * state — alone.
 *
 * Kept as a pure function so the behaviour is testable without mounting a grid.
 */

import type { ScreenerRow } from "@qufolio/transport";

export interface RowTransaction {
  add: ScreenerRow[];
  update: ScreenerRow[];
  remove: ScreenerRow[];
}

export interface RowDiff {
  transaction: RowTransaction;
  /** Ids present after applying the transaction — the next diff's baseline. */
  nextIds: Set<string>;
}

/** The id AG Grid will derive for a row, as a string (its `getRowId` contract). */
export function rowId(row: ScreenerRow, idField: string): string {
  return String(row[idField] ?? "");
}

/**
 * Diff a fresh snapshot against the ids currently in the grid.
 *
 * Rows whose id is already present become updates; new ids are adds; ids that
 * vanished are removed (as id-only stubs, which is all AG Grid needs to locate
 * them). Duplicate ids within one snapshot are collapsed — last row wins —
 * because AG Grid rejects a transaction containing the same id twice.
 */
export function diffRows(
  previousIds: ReadonlySet<string>,
  rows: readonly ScreenerRow[],
  idField: string,
): RowDiff {
  const byId = new Map<string, ScreenerRow>();
  for (const row of rows) byId.set(rowId(row, idField), row);

  const add: ScreenerRow[] = [];
  const update: ScreenerRow[] = [];
  for (const [id, row] of byId) {
    (previousIds.has(id) ? update : add).push(row);
  }

  const remove: ScreenerRow[] = [];
  for (const id of previousIds) {
    if (!byId.has(id)) remove.push({ [idField]: id });
  }

  return { transaction: { add, update, remove }, nextIds: new Set(byId.keys()) };
}

/**
 * Choose the field that identifies a row.
 *
 * `symbol` on a detail frame; on a rollup frame the first text field, which is
 * the group key the server aggregated by. Returns `undefined` when a frame has
 * no text column at all — the caller must then skip `getRowId` rather than
 * invent one, since a wrong id is worse than none.
 */
export function resolveRowIdField(
  fields: readonly { name: string; type: string }[],
  explicit?: string,
): string | undefined {
  if (explicit) return explicit;
  if (fields.some((f) => f.name === "symbol")) return "symbol";
  return fields.find((f) => f.type === "text")?.name;
}
